import React, { useState, useEffect } from 'react';
import ServicePicker from 'components/ServicePicker';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { translate } from 'components/Translation';
import PriceCell from '../common/cells/PriceCell';
import numeral from 'numeral';
import SimpleValueCell from '../common/cells/SimpleValueCell';
import { CustomAttribute, extractAttributeValue } from 'utils/ecommerce/services/extract';
import Unlimited from '@material-ui/icons/AllInclusive';
import ValueCell from '../common/cells/ValueCell';
import DividedCell from '../common/cells/DividedCell';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';

interface IPSTNTableProps {
	selectedTab: string | null;
	setSelectedTab: (value: types.ecommerce.CatalogEntryView | null) => void;
	partNumber?: string;
}

const PSTNTable = (props: IPSTNTableProps) => {
	const { selectedTab, setSelectedTab, partNumber } = props;
	const [plans, setPlans] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		setLoading(true);
		ECommerceService.getGroupContent(idConfig.PSTN.PLANS[env]).subscribe(
			({
				response: { catalogEntryView },
			}: {
				response: { catalogEntryView: types.ecommerce.CatalogEntryView[] };
			}) => {
				setPlans(catalogEntryView);
				if (catalogEntryView.length && partNumber) {
					const selectedPlan = catalogEntryView.find((plan) => plan.partNumber === partNumber);
					if (!!selectedPlan) {
						setSelectedTab(selectedPlan);
					}
				}
				setLoading(false);
			}
		);
	}, [partNumber, setSelectedTab]);

	useEffect(() => {
		if (plans.length && partNumber) {
			const selectedPlan = plans.find((plan) => plan.partNumber === partNumber);
			if (!!selectedPlan) {
				setSelectedTab(selectedPlan);
			}
		}
	}, [plans, setSelectedTab, partNumber]);

	return (
		<div>
			{loading ? (
				<CustomCircularProgress text="" loading transparent />
			) : (
				<ServicePicker
					selectedTabColor="success"
					selectedTab={plans.findIndex((plan: any) => selectedTab === plan.uniqueID)}
					setSelectedTab={(index: number | null) => {
						setSelectedTab(index === null ? null : plans[index!]);
					}}
					tabs={plans.map((plan: any) => {
						const includedMinutes = extractAttributeValue(plan, CustomAttribute.INCLUDED_MINUTES);
						const prices = plan?.price || [];

						const price = {
							offer: prices.find((singlePrice: any) => singlePrice.usage === 'Offer'),
							display: prices.find((singlePrice: any) => singlePrice.usage === 'Display'),
						};

						const cells = [
							<SimpleValueCell
								key="LOC_MIN_COST"
								value={extractAttributeValue(plan, CustomAttribute.LOC_MIN_COST)}
								backgroundColor="backgroundGray"
								subtitle={translate('services.table.pstn.LOC_MIN_COST')}
							/>,
							<DividedCell
								key="PEAK_OFF_PEAK"
								cells={[
									{
										title: translate('services.table.pstn.PEAK'),
										value: extractAttributeValue(plan, CustomAttribute.CALL_PRICE_PEAK),
									},
									{
										title: translate('services.table.pstn.OFF_PEAK'),
										value: extractAttributeValue(plan, CustomAttribute.CALL_PRICE_OFF_PEAK),
									},
								]}
								title={translate('services.table.pstn.FIXED_CALLS')}
							/>,
							includedMinutes === 'Unlimited' ? (
								<ValueCell
									key="INCLUDED_MINUTES"
									backgroundColor="backgroundGray"
									value={<Unlimited style={{ height: 18, width: 18 }} />}
									title={translate('services.table.pstn.INCLUDED_MINUTES')}
								/>
							) : (
								<SimpleValueCell
									backgroundColor="backgroundGray"
									value={includedMinutes}
									key="INCLUDED_MINUTES"
									title={translate('services.table.pstn.INCLUDED_MINUTES')}
								/>
							),
						];
						return {
							column: (
								<>
									{cells}
									<PriceCell
										price={numeral(price.display.value).format('0.000')}
										currency={price.display.currency}
										offerPrice={price.offer}
										perMonth
										backgroundColor="white"
									/>
								</>
							),
							numberOfCells: 4,
							header: {
								title: plan.name,
							},
						};
					})}
					numberOfTabsOnSlide={Math.max(plans.length, 1)}
				/>
			)}
		</div>
	);
};

export default PSTNTable;
